import React, { useEffect, useState } from 'react'
import { useTrans } from '../../../../services/i18n'
import { Paper, SpacingContainer } from '../../../../components/common/Containers'
import { Grid, IconButton } from '@material-ui/core'
import Typography from '../../../../components/common/Typography'
import { Divider } from '../../../../components/common/Divider'
import { Button } from '../../../../components/common/Buttons'
import { TextField } from '../../../../components/common/Inputs'
import Loader from '../../../../components/common/Loader'
import SetupResourceModal from './setupResourceModal'
import { IconDelete } from '../../../../components/svg/Icons'
import EditIcon from '@mui/icons-material/Edit'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import useBooleanHandlers from '../../../../hooks/useBooleanHandlers'
import ConfirmDialog from '../../../../components/generics/Confirm/Dialog'
import connect from '../../connect'

const GovernmentResources = ({
    governmentResources = [],
    getGovernmentResourcesList,
    createGovernmentResource,
    updateGovernmentResource,
    deleteGovernmentResource
}) => {
    const { trans } = useTrans()
    const [loading, setLoading] = useState(false)
    const [editId, setEditId] = useState(null)
    const [form, setForm] = useState({ name: '', url: '' })
    const [isAddOpen, setAddOpen] = useState(false)
    const [openRemove, onOpenRemove, onCloseRemove] = useBooleanHandlers()
    const [pendingRemove, setPendingRemove] = useState(false)
    const [resourceToDelete, setResourceToDelete] = useState(null)

    useEffect(() => {
        setLoading(true)
        getGovernmentResourcesList().finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (editId) {
            const res = governmentResources.find(r => r.id === editId) || {}
            setForm({ name: res.name || '', url: res.url || '' })
        } else {
            setForm({ name: '', url: '' })
        }
    }, [editId, governmentResources])

    const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value })

    const handleSaveEdit = async () => {
        setLoading(true)
        await updateGovernmentResource({ id: editId, ...form })
        await getGovernmentResourcesList()
        setEditId(null)
        setLoading(false)
    }

    const confirmDelete = id => {
        setResourceToDelete(id)
        onOpenRemove()
    }

    const onConfirmDelete = async () => {
        setPendingRemove(true)
        await deleteGovernmentResource({ id: resourceToDelete })
        await getGovernmentResourcesList()
        setPendingRemove(false)
        onCloseRemove()
        setResourceToDelete(null)
    }

    const openAddModal = () => setAddOpen(true)
    const closeAddModal = () => setAddOpen(false)

    return (
        <SpacingContainer py={2}>
            <SetupResourceModal
                open={isAddOpen}
                onClose={closeAddModal}
                createGovernmentResource={createGovernmentResource}
                afterSave={() => {
                    closeAddModal()
                    getGovernmentResourcesList()
                }}
            />

            <Paper component={SpacingContainer} py={3} px="5sm3">
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Grid item xs zeroMinWidth>
                        <Typography
                            style={{ alignItems: 'center', display: 'flex' }}
                            variant="h4"
                            weight="500"
                            fontFamily="lexend"
                        >
                            <BusinessCenterIcon style={{ marginRight: '5px' }} />
                            {trans('government-resources')}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={openAddModal}>
                            {trans('add-resource')}
                        </Button>
                    </Grid>
                </Grid>

                <Divider light style={{ margin: '20px 0' }} />

                {loading && <Loader open={loading} />}

                {!loading && governmentResources.map(res => (
                    <Grid
                        container
                        key={res.id}
                        alignItems="center"
                        spacing={2}
                        style={{ padding: '8px 0' }}
                    >
                        <Grid item xs={4}>
                            {editId === res.id
                                ? <TextField name="name" value={form.name} onChange={handleChange} fullWidth />
                                : <Typography noWrap>{res.name}</Typography>}
                        </Grid>

                        <Grid item xs={4}>
                            {editId === res.id
                                ? <TextField name="url" value={form.url} onChange={handleChange} fullWidth />
                                : <Typography noWrap>{res.url}</Typography>}
                        </Grid>

                        <Grid
                            item
                            xs={4}
                            container
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            {editId === res.id ? (
                                <>
                                    <Button
                                        variant="contained"
                                        pending={loading}
                                        onClick={handleSaveEdit}
                                    >
                                        {trans('save')}
                                    </Button>
                                    <Button onClick={() => setEditId(null)}>
                                        {trans('cancel')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <IconButton
                                        onClick={() => {
                                            setEditId(res.id)
                                            setForm({ name: res.name, url: res.url })
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => confirmDelete(res.id)}>
                                        <IconDelete />
                                    </IconButton>
                                </>
                            )}
                        </Grid>
                    </Grid>
                ))}
            </Paper>

            <ConfirmDialog
                open={openRemove}
                onClose={onCloseRemove}
                onConfirm={onConfirmDelete}
                pending={pendingRemove}
                title={trans('remove-resource')}
                hideIcon={true}
                note={trans('this-url-will-no-longer-appear-on-the-state-and-local-opportunities-page')}
                confirmType={true}
                text={`${trans('delete-this-resource')}?`}
            />
        </SpacingContainer>
    )
}

export default connect(GovernmentResources)


